import type { MapDef } from './MapDef.js';

// V1.5+ Crucible — the headline larger map with a 7-relay graph topology.
//
// Identity (vs Foundry / Vault / Canyon):
//   - Foundry / Vault / Canyon are 2000 × 2000 with 3 relays on a single
//     chain (north → mid → south). The signal network is essentially a
//     line: one BFS path from Core to Core. "Cutting a link" almost
//     always means cutting the mid relay.
//   - Crucible is 3000 × 2400 with SEVEN relays arranged as a graph:
//     two HOME relays per side (close to each Core) plus a contested
//     middle band of three (two SHOULDERS at west/east plus the APEX
//     at centre). The result is multiple BFS paths from Core to Core,
//     so cutting any single relay still leaves alternates open. This
//     is the layer-5/6/7 "network as infrastructure" promise from
//     docs/02_GAME_DESIGN.md §4.1 finally landing on a single map.
//
// Map invariants (must hold for every V1.5+ map):
//   - 32-px tile.
//   - Two cores (one per side); odd number of control nodes.
//   - Signal-link chain (V1.5+ §11 Option A) viable end-to-end with the
//     SIGNAL_LINK_RANGE_PX = 700 bump introduced for Crucible.
//   - Camp anchor at the apex relay (1500, 1200) so CampSystem behaves
//     identically to the V1.5 maps (Foundry / Vault / Canyon anchor at
//     their mid relay).
//
// Signal-link distance audit (every adjacent pair must be ≤ 700 px):
//   Core 0 (1500, 300)  ↔ home_w_p0 (1100, 600): √(400² + 300²) = 500 ✓
//   Core 0 (1500, 300)  ↔ home_e_p0 (1900, 600): mirror              500 ✓
//   home_w_p0 (1100, 600) ↔ mid_w (850, 1200):  √(250² + 600²) ≈ 650 ✓
//   home_e_p0 (1900, 600) ↔ mid_e (2150, 1200): mirror               650 ✓
//   mid_w (850, 1200)  ↔ apex (1500, 1200):     √(650² + 0²)   = 650 ✓
//   mid_e (2150, 1200) ↔ apex (1500, 1200):     mirror               650 ✓
//   mid_w (850, 1200)  ↔ home_w_p1 (1100, 1800): √(250² + 600²) ≈ 650 ✓
//   mid_e (2150, 1200) ↔ home_e_p1 (1900, 1800): mirror              650 ✓
//   home_w_p1 (1100, 1800) ↔ Core 1 (1500, 2100): √(400² + 300²) = 500 ✓
//   home_e_p1 (1900, 1800) ↔ Core 1: mirror                          500 ✓
//
// Forbidden hops (must be > 700 px so the chain still requires multiple
// owned relays — preserves §11 "no lone-wolf mid claim"):
//   Core 0 ↔ mid_w:  √(650² + 900²) ≈ 1110 ✓
//   Core 0 ↔ apex:   √(0² + 900²)   = 900  ✓
//   Core 0 ↔ mid_e:  mirror of mid_w        ≈ 1110 ✓
//   Core 0 ↔ Core 1: 1800                    ✓
//   home_w_p0 ↔ apex: √(400² + 600²) ≈ 721 ✓ (just past the threshold; still
//     forces a hop through mid_w)
//   home_w_p0 ↔ home_e_p0: 800              ✓
//   home_w_p0 ↔ home_w_p1: 1200             ✓
//
// Graph topology — multiple paths from P0 to P1 via owned relays:
//   1) P0 → home_w_p0 → mid_w → apex → mid_e → home_e_p1 → P1
//   2) P0 → home_w_p0 → mid_w → home_w_p1 → P1     (bypasses apex)
//   3) P0 → home_e_p0 → mid_e → apex → mid_w → home_w_p1 → P1
//   4) P0 → home_e_p0 → mid_e → home_e_p1 → P1     (bypasses apex)
// So cutting the apex leaves both flank paths open; cutting one flank
// shoulder leaves the apex bridge + the opposite flank.
//
// Authoring rules (same as every map):
//   - Stay clear of the 120 px capture radius of every relay.
//   - Stay clear of the 200 px build-slot ring around every Core.
//   - Stay clear of squad spawn points (60 px).
//   - No obstacle intrudes closer than CACHE_PICKUP_RADIUS_PX − 10 = 80 px
//     from the camp anchor.
//   - All obstacles are positive-area rects.
export const CRUCIBLE: MapDef = {
  id: 'crucible',
  name: 'Crucible',
  width: 3000,
  height: 2400,
  tilePx: 32,
  cores: [
    { ownerId: 0, pos: { x: 1500, y: 300 } },
    { ownerId: 1, pos: { x: 1500, y: 2100 } },
  ],
  controlNodes: [
    { name: 'home_w_p0', pos: { x: 1100, y: 600 } },
    { name: 'home_e_p0', pos: { x: 1900, y: 600 } },
    { name: 'mid_w',     pos: { x: 850,  y: 1200 } },
    { name: 'apex',      pos: { x: 1500, y: 1200 } },
    { name: 'mid_e',     pos: { x: 2150, y: 1200 } },
    { name: 'home_w_p1', pos: { x: 1100, y: 1800 } },
    { name: 'home_e_p1', pos: { x: 1900, y: 1800 } },
  ],
  obstacles: [
    // --- Core-side flank walls (each Core gets a shoulder so attackers
    //     can't see straight through to the Core after slipping past a
    //     home relay). Stay 200 px clear of each Core's build-slot ring.
    { x: 1320, y: 460, w: 360, h: 60 },   // P0 forward bunker
    { x: 1320, y: 1880, w: 360, h: 60 },  // P1 forward bunker (mirror)

    // --- Home-relay flanking covers (give Engineer mini-turrets a nest
    //     position behind each home relay without intruding on the 120 px
    //     capture radius).
    //     P0 north side.
    { x: 800, y: 720, w: 100, h: 60 },    // dist to home_w_p0 (1100, 600) ≈ 322 ✓
    { x: 2100, y: 720, w: 100, h: 60 },   // dist to home_e_p0 (mirror)   ≈ 322 ✓
    //     P1 south side (mirror).
    { x: 800, y: 1620, w: 100, h: 60 },
    { x: 2100, y: 1620, w: 100, h: 60 },

    // --- Mid-band flank walls (force squads moving between mid_w/apex/mid_e
    //     to commit to a side rather than walk a straight line).
    { x: 1100, y: 1100, w: 160, h: 60 },  // between mid_w & apex
    { x: 1740, y: 1240, w: 160, h: 60 },  // between apex & mid_e (offset)

    // --- Apex defender cover (behind the centre relay, away from camp).
    //     Camp anchor sits AT apex (1500, 1200); CACHE_PICKUP_RADIUS_PX is
    //     ~ 90 px so we stay > 100 px clear.
    { x: 1440, y: 1320, w: 120, h: 50 },  // dist to apex ≈ 145 px ✓

    // --- Side flank lanes for Recon side-routes.
    //     West shoulder (mid_w at 850, 1200).
    { x: 500, y: 940, w: 120, h: 60 },    // dist to mid_w ≈ 397 px ✓
    { x: 500, y: 1420, w: 120, h: 60 },   // dist to mid_w ≈ 397 px ✓ (mirror y)
    //     East shoulder (mid_e at 2150, 1200) — mirrors.
    { x: 2380, y: 940, w: 120, h: 60 },
    { x: 2380, y: 1420, w: 120, h: 60 },

    // --- Pylon clusters that serve as engineer nest options on the
    //     vertical lanes between home relays and shoulders.
    { x: 700, y: 1000, w: 60, h: 50 },    // west vertical lane
    { x: 2240, y: 1000, w: 60, h: 50 },   // east vertical lane (mirror)
    { x: 700, y: 1380, w: 60, h: 50 },    // mirror y
    { x: 2240, y: 1380, w: 60, h: 50 },
  ],
  spawns: [
    { ownerId: 0, pos: { x: 1420, y: 380 } },
    { ownerId: 1, pos: { x: 1580, y: 2020 } },
  ],
  // Apex (the contested centre) anchors the V1.5 neutral camp, mirroring
  // every existing map's "mid-relay = camp" convention.
  neutralCampAnchor: { x: 1500, y: 1200 },
};
